/**
 * Performance utilities for memory leak prevention and optimization
 */

// Global cleanup registry
const cleanupFunctions: (() => void)[] = []

/**
 * Register a cleanup function to be called on app unmount
 */
export function registerCleanup(cleanupFn: () => void) {
  cleanupFunctions.push(cleanupFn)
}

/**
 * Execute all registered cleanup functions
 */
export function executeCleanup() {
  console.log('🧹 Executing global cleanup...')
  cleanupFunctions.forEach((cleanup, index) => {
    try {
      cleanup()
      console.log(`✅ Cleanup ${index + 1} completed`)
    } catch (error) {
      console.error(`❌ Cleanup ${index + 1} failed:`, error)
    }
  })
  cleanupFunctions.length = 0
}

/**
 * Setup global cleanup listeners
 */
export function setupGlobalCleanup() {
  if (typeof window === 'undefined') return

  // Page unload cleanup
  const handleBeforeUnload = () => {
    executeCleanup()
  }

  // Visibility change cleanup (when tab becomes hidden)
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // Cleanup old data when tab is hidden
      setTimeout(() => {
        if (document.visibilityState === 'hidden') {
          console.log('🧹 Tab hidden cleanup executed')
          // Clear old notifications
          if ((window as any).__notificationCleanupInterval) {
            try {
              // Dynamic import instead of require
              import('@/store/notificationStore').then(({ useNotificationStore }) => {
                const store = useNotificationStore.getState()
                if (store.clearOldNotifications) {
                  store.clearOldNotifications()
                }
              }).catch(() => {
                // Notification store might not exist yet
                console.log('Notification store not available for cleanup')
              })
            } catch (error) {
              console.log('Notification cleanup skipped:', error)
            }
          }
        }
      }, 5000) // 5 saniye sonra temizle
    }
  }

  window.addEventListener('beforeunload', handleBeforeUnload)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Register cleanup for these listeners
  registerCleanup(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  console.log('🔧 Global cleanup listeners registered')
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      if (!immediate) func(...args)
    }

    const callNow = immediate && !timeout
    
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    
    if (callNow) func(...args)
  }
}

/**
 * Throttle function for performance optimization
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * Memory usage checker
 */
export function checkMemoryUsage(): {
  used: number
  total: number
  limit: number
  percentage: number
} | null {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return null
  }

  // @ts-ignore - memory API experimental
  if (!performance.memory) {
    return null
  }

  // @ts-ignore
  const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = performance.memory

  return {
    used: Math.round(usedJSHeapSize / 1048576), // MB
    total: Math.round(totalJSHeapSize / 1048576), // MB
    limit: Math.round(jsHeapSizeLimit / 1048576), // MB
    percentage: Math.round((usedJSHeapSize / jsHeapSizeLimit) * 100)
  }
}

/**
 * Force garbage collection (development only)
 */
export function forceGarbageCollection() {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    // @ts-ignore - gc is available in Chrome DevTools
    if (window.gc) {
      window.gc()
      console.log('🗑️ Garbage collection forced')
    } else {
      console.warn('⚠️ Garbage collection not available. Enable in Chrome DevTools.')
    }
  }
}

/**
 * Performance metrics collector
 */
export class PerformanceCollector {
  private static instance: PerformanceCollector
  private metrics: Map<string, number[]> = new Map()

  static getInstance(): PerformanceCollector {
    if (!PerformanceCollector.instance) {
      PerformanceCollector.instance = new PerformanceCollector()
    }
    return PerformanceCollector.instance
  }

  recordMetric(name: string, value: number) {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, [])
    }
    
    const values = this.metrics.get(name)!
    values.push(value)
    
    // Keep only last 100 values
    if (values.length > 100) {
      values.shift()
    }
  }

  getMetrics(name: string): {
    average: number
    min: number
    max: number
    count: number
  } | null {
    const values = this.metrics.get(name)
    if (!values || values.length === 0) {
      return null
    }

    return {
      average: values.reduce((a, b) => a + b, 0) / values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      count: values.length
    }
  }

  getAllMetrics(): Record<string, any> {
    const result: Record<string, any> = {}
    
    for (const [name, values] of this.metrics.entries()) {
      if (values.length > 0) {
        result[name] = {
          average: values.reduce((a, b) => a + b, 0) / values.length,
          min: Math.min(...values),
          max: Math.max(...values),
          count: values.length,
          recent: values.slice(-10) // Son 10 değer
        }
      }
    }
    
    return result
  }

  clearMetrics() {
    this.metrics.clear()
    console.log('📊 Performance metrics cleared')
  }
}